/**
 * ScopedBiblePlans — admin & member view of a tenant's reading plans.
 *
 * Used by SchoolModule (sidebar item "Bible Plans"), EventAdmin, and
 * Church admin. One component, three contexts.
 *
 * Members can browse + start a plan + see their own progress.
 * Tenant admins can additionally create / edit / delete plans and view
 * every reader's progress dashboard.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MediaUpload } from '@/components/MediaUpload';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import {
  BookOpen, Plus, Trash2, Settings, Users, Flame, CheckCircle2,
  Calendar, Loader2, BarChart3, UserPlus, Search, Check, Wrench,
  Sparkles, RotateCcw,
} from 'lucide-react';
import BiblePlanDayBuilder, { cleanForApi } from '@/components/bible/BiblePlanDayBuilder';
import BiblePlanHealthDialog from '@/components/bible/BiblePlanHealthDialog';
import { authHeaders } from '@/lib/authToken';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Request failed (${res.status})`);
  }
  return res.json();
};

export default function ScopedBiblePlans({ scopeType, scopeId, isAdmin = false, tenantName = '' }) {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [editPlan, setEditPlan] = useState(null);
  const [progressFor, setProgressFor] = useState(null);
  const [assignFor, setAssignFor] = useState(null);
  const [healthFor, setHealthFor] = useState(null);
  const [form, setForm] = useState({
    title: '', description: '', duration_days: 30, category: 'general', is_public: true, image_url: '',
  });
  const [days, setDays] = useState([]);

  const { data, isLoading } = useQuery({
    queryKey: ['scoped-plans', scopeType, scopeId],
    queryFn: () => api(`/api/bible/scope/${scopeType}/${scopeId}/plans`),
    enabled: !!scopeType && !!scopeId,
  });

  const createMutation = useMutation({
    mutationFn: (payload) => api(`/api/admin/bible/scope/${scopeType}/${scopeId}/plans`, {
      method: 'POST', body: JSON.stringify(payload),
    }),
    onSuccess: (res) => {
      const saved = res?.days_saved || 0;
      toast.success(`Reading plan created · ${saved} day${saved === 1 ? '' : 's'}`);
      setShowCreate(false);
      setForm({ title: '', description: '', duration_days: 30, category: 'general', is_public: true, image_url: '' });
      setDays([]);
      qc.invalidateQueries({ queryKey: ['scoped-plans', scopeType, scopeId] });
    },
    onError: (e) => toast.error(e.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => api(`/api/admin/bible/scope/plans/${id}`, {
      method: 'PUT', body: JSON.stringify(payload),
    }),
    onSuccess: (res) => {
      toast.success(`Plan updated${res?.days_saved ? ` · ${res.days_saved} day${res.days_saved === 1 ? '' : 's'}` : ''}`);
      setEditPlan(null);
      setDays([]);
      qc.invalidateQueries({ queryKey: ['scoped-plans', scopeType, scopeId] });
    },
    onError: (e) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api(`/api/admin/bible/scope/plans/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Plan removed');
      qc.invalidateQueries({ queryKey: ['scoped-plans', scopeType, scopeId] });
    },
    onError: (e) => toast.error(e.message),
  });

  const startMutation = useMutation({
    mutationFn: (planId) => api(`/api/bible/plans/${planId}/start`, {
      method: 'POST', body: JSON.stringify({ reading_plan_id: planId }),
    }),
    onSuccess: () => {
      toast.success('Plan started — happy reading!');
      qc.invalidateQueries({ queryKey: ['scoped-plans', scopeType, scopeId] });
    },
    onError: (e) => toast.error(e.message),
  });

  const completeDayMutation = useMutation({
    mutationFn: ({ userPlanId, dayNumber }) => api(`/api/bible/my-plans/${userPlanId}/complete-day`, {
      method: 'POST', body: JSON.stringify({ day_number: dayNumber }),
    }),
    onSuccess: () => {
      toast.success('Day marked complete · streak +1');
      qc.invalidateQueries({ queryKey: ['scoped-plans', scopeType, scopeId] });
    },
    onError: (e) => toast.error(e.message),
  });

  const allPlans = data?.plans || [];
  const plans = allPlans.filter((p) => p.source !== 'central');
  const centralPlans = allPlans.filter((p) => p.source === 'central');

  return (
    <div className="space-y-4" data-testid="scoped-bible-plans">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-indigo-600" /> Bible Reading Plans
          </h2>
          <p className="text-xs text-slate-500">
            {tenantName || data?.tenant?.name || 'Your community'} · {plans.length} plan{plans.length === 1 ? '' : 's'}
          </p>
        </div>
        {isAdmin && (
          <Button size="sm" onClick={() => setShowCreate(true)} data-testid="bible-plan-new-btn">
            <Plus className="h-4 w-4 mr-1" /> New plan
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>
      ) : plans.length === 0 ? (
        <Card><CardContent className="p-6 text-center text-slate-500">
          {isAdmin ? 'No plans yet — create the first one for your community.' : 'No reading plans available yet.'}
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {plans.map((p) => {
            const prog = p.progress;
            return (
              <Card key={p.id || p._id} className="hover:shadow-md transition-shadow" data-testid={`bible-plan-card-${p.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-sm">{p.title}</CardTitle>
                      <p className="text-xs text-slate-500 mt-0.5">{p.duration_days} days · {p.category || 'general'}</p>
                    </div>
                    {isAdmin && (
                      <div className="flex gap-1 shrink-0">
                        <Button variant="ghost" size="icon" className="h-7 w-7" title="Edit" onClick={async () => {
                          setForm({ title: p.title, description: p.description, duration_days: p.duration_days, category: p.category || 'general', is_public: p.is_public !== false, image_url: p.image_url || '' });
                          setEditPlan(p);
                          try {
                            const full = await api(`/api/admin/bible/scope/plans/${p.id}`);
                            setDays(full?.days || []);
                          } catch (_e) { setDays([]); }
                        }} data-testid={`bible-plan-edit-${p.id}`}>
                          <Settings className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" title="Reader progress" onClick={() => setProgressFor(p)} data-testid={`bible-plan-progress-${p.id}`}>
                          <BarChart3 className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-700 hover:bg-emerald-50" title="Assign to people" onClick={() => setAssignFor(p)} data-testid={`bible-plan-assign-${p.id}`}>
                          <UserPlus className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost" size="icon" className="h-7 w-7 text-blue-600 hover:bg-blue-50"
                          title="Repair assignments (for students who were assigned before iter 317)"
                          onClick={async () => {
                            if (!window.confirm("Re-link this plan's existing assignments to the students' login accounts? Safe to run multiple times.")) return;
                            try {
                              const r = await api.post(`/admin/bible/scope/plans/${p.id}/repair-assignments`);
                              const d = r.data || {};
                              toast.success(`Repaired ${d.repaired || 0} assignment(s) · ${d.skipped || 0} already OK`);
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || 'Repair failed');
                            }
                          }}
                          data-testid={`bible-plan-repair-${p.id}`}
                        >
                          <Wrench className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost" size="icon" className="h-7 w-7 text-violet-700 hover:bg-violet-50"
                          title="Generate a weekly pop quiz from this plan's recent readings (Copilot · review before publish)"
                          onClick={async () => {
                            try {
                              const r = await fetch(`${API_URL}/api/admin/bible/scope/plans/${p.id}/weekly-pop-quiz`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                                body: JSON.stringify({ days: 7, num_questions: 8, audience: 'youth', bible_version: 'NIV' }),
                              });
                              const d = await r.json();
                              if (!r.ok || d.ok === false) {
                                toast.error(d.detail || d.error || 'Could not draft pop quiz');
                              } else {
                                toast.success(`Pop quiz drafted — review in Copilot Proposals${d.proposal_id ? ` (#${String(d.proposal_id).slice(-6)})` : ''}`);
                              }
                            } catch (e) {
                              toast.error(e?.message || 'Pop quiz failed');
                            }
                          }}
                          data-testid={`bible-plan-popquiz-${p.id}`}
                        >
                          <Sparkles className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost" size="icon" className="h-7 w-7 text-orange-600 hover:bg-orange-50"
                          title="Health check — detect glitched streaks & reset (streak-only or full)"
                          onClick={() => setHealthFor(p)}
                          data-testid={`bible-plan-reset-${p.id}`}
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600 hover:bg-rose-50" title="Delete" onClick={() => { if (window.confirm(`Delete "${p.title}"?`)) deleteMutation.mutate(p.id); }} data-testid={`bible-plan-delete-${p.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  {p.description && <p className="text-xs text-slate-600 mb-2 line-clamp-2">{p.description}</p>}
                  {prog ? (
                    <div className="space-y-1.5" data-testid={`bible-plan-progress-${p.id}-bar`}>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-600">Day {prog.current_day} of {p.duration_days}</span>
                        <span className="flex items-center gap-2">
                          <span className="flex items-center gap-0.5 text-amber-600"><Flame className="h-3 w-3" /> {prog.streak}</span>
                          <Badge variant="outline" className="text-[10px]">{prog.completion_percent}%</Badge>
                        </span>
                      </div>
                      <Progress value={prog.completion_percent} className="h-1.5" />
                      <div className="flex gap-1.5 mt-1">
                        <Button
                          asChild
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          data-testid={`bible-plan-open-${p.id}`}
                        >
                          <a href={`/bible/plans/${p.id}/read?from=${scopeType}&scopeId=${scopeId}`}>
                            <BookOpen className="h-3.5 w-3.5 mr-1" /> Open in Bible
                          </a>
                        </Button>
                        <Button
                          size="sm"
                          variant={prog.completed_days?.includes(prog.current_day) ? 'outline' : 'default'}
                          disabled={completeDayMutation.isPending}
                          onClick={() => completeDayMutation.mutate({ userPlanId: prog.user_plan_id, dayNumber: prog.current_day })}
                          data-testid={`bible-plan-complete-${p.id}`}
                          title={`Mark Day ${prog.current_day} complete`}
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button size="sm" className="w-full" disabled={startMutation.isPending} onClick={() => startMutation.mutate(p.id)} data-testid={`bible-plan-start-${p.id}`}>
                      <BookOpen className="h-3.5 w-3.5 mr-1" /> Start plan
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit dialog */}
      <Dialog open={showCreate || !!editPlan} onOpenChange={(o) => { if (!o) { setShowCreate(false); setEditPlan(null); setDays([]); } }}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bible-plan-edit-dialog">
          <DialogHeader><DialogTitle>{editPlan ? 'Edit reading plan' : 'New reading plan'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. 30 days through John" data-testid="bible-plan-title-input" /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="One sentence describing the journey" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Duration (days)</Label><Input type="number" min={1} max={365} value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: Number(e.target.value) || 30 })} data-testid="bible-plan-duration-input" /></div>
              <div><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="wisdom / discipleship / fasting…" /></div>
            </div>
            <MediaUpload value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} purpose="cover" label="Cover image (optional)" testId="bible-plan-image-upload" />
            <div className="border-t pt-3" data-testid="bible-plan-days-section">
              <div className="mb-2">
                <Label className="text-sm font-semibold">Daily readings</Label>
                <p className="text-xs text-slate-500">Add one or more passages per day. Members will see exactly these readings on the matching day.</p>
              </div>
              <BiblePlanDayBuilder value={days} onChange={setDays} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); setEditPlan(null); setDays([]); }}>Cancel</Button>
            <Button
              disabled={!form.title || createMutation.isPending || updateMutation.isPending}
              onClick={() => {
                const cleanDays = cleanForApi(days);
                const payload = { ...form, days: cleanDays };
                if (editPlan) updateMutation.mutate({ id: editPlan.id, payload });
                else createMutation.mutate(payload);
              }}
              data-testid="bible-plan-save-btn"
            >
              {(createMutation.isPending || updateMutation.isPending) ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              {editPlan ? 'Save changes' : 'Create plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Central library — one plan library for the whole ecosystem. Created
          in the Bible module, assignable here without recreating it. */}
      {centralPlans.length > 0 && (
        <div className="pt-2" data-testid="central-plan-library">
          <div className="flex items-baseline justify-between gap-3">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-amber-600" /> From the central library
            </h3>
            <span className="text-xs text-slate-500">{centralPlans.length} plans</span>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Published once in the Bible module and available everywhere — assign one to your
            people and their progress follows them across the ecosystem.
          </p>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            {centralPlans.map((p) => (
              <Card key={p.id} data-testid={`central-plan-${p.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-sm truncate">{p.title}</p>
                      <p className="text-xs text-slate-500">
                        {p.duration_days} days{p.category ? ` · ${p.category}` : ''}
                        {p.subscribers_count ? ` · ${p.subscribers_count} enrolled` : ''}
                      </p>
                    </div>
                    <Badge variant="outline" className="shrink-0 text-amber-700 border-amber-300">
                      Central
                    </Badge>
                  </div>
                  {p.description && (
                    <p className="mt-2 text-xs text-slate-600 line-clamp-2">{p.description}</p>
                  )}
                  <div className="mt-4 flex flex-wrap gap-2">
                    {isAdmin && (
                      <Button size="sm" onClick={() => setAssignFor(p)}
                              data-testid={`central-assign-${p.id}`}>
                        Assign to my people
                      </Button>
                    )}
                    <Button size="sm" variant="outline" asChild data-testid={`central-read-${p.id}`}>
                      <a href={`/bible/plans/${p.id}/read?from=${scopeType}&scopeId=${scopeId}`}>
                        Read
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Reader progress dashboard */}
      {progressFor && <PlanProgressDialog plan={progressFor} onClose={() => setProgressFor(null)} />}

      {/* Reading health check + granular reset */}
      {healthFor && (
        <BiblePlanHealthDialog
          plan={healthFor}
          scopeType={scopeType}
          scopeId={scopeId}
          onClose={() => setHealthFor(null)}
          onReset={() => qc.invalidateQueries({ queryKey: ['scoped-plans', scopeType, scopeId] })}
        />
      )}

      {/* Assign plan to people */}
      {assignFor && (
        <AssignPlanDialog
          plan={assignFor}
          scopeType={scopeType}
          scopeId={scopeId}
          onClose={() => setAssignFor(null)}
          onSuccess={() => qc.invalidateQueries({ queryKey: ['scoped-plans', scopeType, scopeId] })}
        />
      )}
    </div>
  );
}


function PlanProgressDialog({ plan, onClose }) {
  const { data } = useQuery({
    queryKey: ['plan-progress', plan.id],
    queryFn: () => api(`/api/admin/bible/scope/plans/${plan.id}/progress`),
    enabled: !!plan?.id,
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="bible-plan-progress-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Users className="h-4 w-4" /> Reader progress · {plan.title}</DialogTitle></DialogHeader>
        {!data ? (
          <div className="text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
              <div className="p-2 rounded bg-indigo-50"><div className="text-xl font-bold text-indigo-700">{data.summary.subscribers}</div><div className="text-xs">Readers</div></div>
              <div className="p-2 rounded bg-emerald-50"><div className="text-xl font-bold text-emerald-700">{data.summary.active}</div><div className="text-xs">Active</div></div>
              <div className="p-2 rounded bg-amber-50"><div className="text-xl font-bold text-amber-700">{data.summary.completed}</div><div className="text-xs">Completed</div></div>
              <div className="p-2 rounded bg-rose-50"><div className="text-xl font-bold text-rose-700">{data.summary.average_completion}%</div><div className="text-xs">Avg progress</div></div>
            </div>
            <div className="max-h-[50vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 sticky top-0"><tr><th className="p-2 text-left">Reader</th><th className="p-2 text-left">Day</th><th className="p-2 text-left">Streak</th><th className="p-2 text-left">Progress</th><th className="p-2 text-left">Last read</th></tr></thead>
                <tbody>
                  {data.rows.length === 0 ? (
                    <tr><td colSpan={5} className="p-4 text-center text-slate-400">No readers have started this plan yet.</td></tr>
                  ) : data.rows.map((r) => (
                    <tr key={r.user_plan_id} className="border-t" data-testid={`reader-row-${r.user_plan_id}`}>
                      <td className="p-2"><div className="font-medium">{r.name}</div><div className="text-xs text-slate-500">{r.email}</div></td>
                      <td className="p-2">{r.current_day}/{r.duration_days}</td>
                      <td className="p-2 flex items-center gap-1"><Flame className="h-3 w-3 text-amber-500" /> {r.streak}</td>
                      <td className="p-2">
                        <div className="flex items-center gap-2">
                          <Progress value={r.completion_percent} className="h-1.5 w-20" />
                          <span className="text-xs">{r.completion_percent}%</span>
                        </div>
                      </td>
                      <td className="p-2 text-xs text-slate-500">{r.last_read_at ? new Date(r.last_read_at).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function AssignPlanDialog({ plan, scopeType, scopeId, onClose, onSuccess }) {
  const qc = useQueryClient();
  const [audience, setAudience] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(new Set());

  const audienceLabel = {
    school: 'All Students',
    event: 'All Delegates',
    church: 'All Members',
  }[scopeType] || 'Everyone';
  const audienceKey = {
    school: 'all_students',
    event: 'all_delegates',
    church: 'all_members',
  }[scopeType] || 'all_members';

  // For schools, the admin can broadcast to several groups, not just students.
  const schoolAudienceOptions = [
    { key: 'all_students', label: 'All Students' },
    { key: 'all_teachers', label: 'All Teachers' },
    { key: 'all_parents', label: 'All Parents' },
    { key: 'all_staff', label: 'All Staff (admins + teachers)' },
    { key: 'all_members', label: 'Everyone in this school' },
  ];

  const { data: people = [], isLoading: peopleLoading } = useQuery({
    queryKey: ['scope-people', scopeType, scopeId, query],
    queryFn: () => api(`/api/admin/bible/scope/${scopeType}/${scopeId}/people?q=${encodeURIComponent(query)}`),
    enabled: !!scopeType && !!scopeId,
  });

  const { data: members = [] } = useQuery({
    queryKey: ['plan-members', plan?.id],
    queryFn: () => api(`/api/admin/bible/scope/plans/${plan.id}/members`),
    enabled: !!plan?.id,
  });

  const alreadyAssigned = new Set((members || []).map((m) => m.user_id));

  const toggle = (uid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const assignMutation = useMutation({
    mutationFn: (payload) => api(`/api/admin/bible/scope/plans/${plan.id}/assign`, {
      // Central library plans belong to no single scope, so we always say
      // which school / church / event we are assigning into.
      method: 'POST', body: JSON.stringify({ ...payload, scope_type: scopeType, scope_id: scopeId }),
    }),
    onSuccess: (res) => {
      toast.success(`Assigned ${res.assigned} · skipped ${res.skipped_already_enrolled} already enrolled`);
      qc.invalidateQueries({ queryKey: ['plan-members', plan.id] });
      qc.invalidateQueries({ queryKey: ['scoped-plans', scopeType, scopeId] });
      setSelected(new Set());
      setAudience('');
      onSuccess?.();
    },
    onError: (e) => toast.error(e.message),
  });

  const submit = () => {
    const payload = {};
    if (audience === 'broadcast') payload.audience = audienceKey;
    else if (audience && audience !== 'pick') payload.audience = audience;
    else payload.user_ids = Array.from(selected);
    if (!payload.audience && (!payload.user_ids || payload.user_ids.length === 0)) {
      toast.error('Pick at least one person or choose the broadcast option');
      return;
    }
    assignMutation.mutate(payload);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bible-plan-assign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserPlus className="h-4 w-4 text-emerald-600" /> Assign · {plan.title}</DialogTitle>
          <DialogDescription>Pick specific people or broadcast this plan to everyone in this {scopeType}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Mode</Label>
            <Select value={audience || 'pick'} onValueChange={(v) => setAudience(v === 'pick' ? '' : v)}>
              <SelectTrigger data-testid="bible-plan-assign-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pick">Pick specific people</SelectItem>
                {scopeType === 'school' ? (
                  schoolAudienceOptions.map(opt => (
                    <SelectItem key={opt.key} value={opt.key}>Assign to {opt.label}</SelectItem>
                  ))
                ) : (
                  <SelectItem value="broadcast">Assign to {audienceLabel}</SelectItem>
                )}
              </SelectContent>
            </Select>
            {audience && audience !== 'pick' && (
              <p className="mt-2 text-xs text-emerald-700 bg-emerald-50 rounded px-3 py-2 border border-emerald-200">
                {audience === 'broadcast'
                  ? `Every person currently in this ${scopeType} (skipping anyone already enrolled) will be added.`
                  : `All matching ${audience.replace('all_','')} (skipping anyone already enrolled) will be added.`}
              </p>
            )}
          </div>

          {audience !== 'broadcast' && !audience.startsWith?.('all_') && (
            <>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by name, email…"
                  className="pl-8"
                  data-testid="bible-plan-assign-search"
                />
              </div>
              <div className="max-h-64 overflow-y-auto border rounded">
                {peopleLoading ? (
                  <div className="p-4 text-center text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>
                ) : people.length === 0 ? (
                  <div className="p-4 text-center text-sm text-slate-400">No people found</div>
                ) : (
                  <ul className="divide-y">
                    {people.map((p) => {
                      const already = alreadyAssigned.has(p.user_id);
                      const checked = selected.has(p.user_id);
                      return (
                        <li
                          key={p.user_id}
                          onClick={() => !already && toggle(p.user_id)}
                          className={`flex items-center gap-3 px-3 py-2 ${already ? 'bg-slate-50 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50'}`}
                          data-testid={`bible-plan-assign-row-${p.user_id}`}
                        >
                          <Checkbox checked={checked || already} disabled={already} />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{p.name}</div>
                            <div className="text-xs text-slate-500 truncate">{p.email}{p.extra ? ` · ${p.extra}` : ''}</div>
                          </div>
                          {already && <Badge variant="outline" className="text-[10px] text-emerald-700 border-emerald-200"><Check className="h-3 w-3 mr-0.5" />Enrolled</Badge>}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <div className="text-xs text-slate-500">
                {selected.size} selected · {alreadyAssigned.size} already enrolled
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={assignMutation.isPending || (!audience && selected.size === 0)}
            data-testid="bible-plan-assign-submit"
          >
            {assignMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            {audience === 'broadcast' ? `Assign to ${audienceLabel}` :
              audience && audience.startsWith('all_') ? `Assign to ${audience.replace('all_','')}` :
              `Assign ${selected.size || ''} ${selected.size === 1 ? 'person' : 'people'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
